import React, { useState, useEffect, useRef } from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Autoplay, EffectFade, Navigation, Pagination } from 'swiper/modules';
import { Link } from 'react-router-dom';
import 'swiper/css';
import 'swiper/css/effect-fade';
import 'swiper/css/navigation';
import 'swiper/css/pagination';

const HeroSlider = ({ featuredProducts = [] }) => {
    const [mousePosition, setMousePosition] = useState({ x: 50, y: 50 });
    const containerRef = useRef(null);
    const { getImageUrl } = require('../utils/api');

    const handleMouseMove = (e) => {
        if (!containerRef.current) return;
        const rect = containerRef.current.getBoundingClientRect();
        const x = ((e.clientX - rect.left) / rect.width) * 100;
        const y = ((e.clientY - rect.top) / rect.height) * 100;
        setMousePosition({ x, y });
    };

    const mainSlide = {
        id: 'main',
        type: "main",
        title: "Complete Barcode Solutions for Your Business",
        subtitle: "Premium barcode printers, scanners, thermal printers, labels, and supplies for retail, manufacturing, logistics, and warehouse operations.",
        image: "https://images.unsplash.com/photo-1581091226825-a6a2a5aee158?w=1600&q=80",
        cta: "Explore Products",
        ctaLink: "/products",
        gradient: "from-blue-800 via-indigo-700 to-blue-900",
        accent: "db-blue",
        stats: [
            { icon: "fas fa-check-circle", text: "Wide Product Range", sub: "500+ products" },
            { icon: "fas fa-shipping-fast", text: "Fast Delivery", sub: "Same-day shipping" }
        ]
    };

    const gradients = [
        "from-blue-800 via-indigo-700 to-blue-900",
        "from-slate-800 via-gray-700 to-slate-900",
        "from-cyan-900 via-blue-800 to-cyan-900",
        "from-indigo-900 via-purple-900 to-indigo-900",
        "from-emerald-800 via-green-700 to-teal-900",
        "from-rose-900 via-red-800 to-pink-900"
    ];

    const slides = [mainSlide, ...featuredProducts.map((product, index) => ({
        id: product._id,
        type: product.category?.name || 'Product',
        title: product.name,
        subtitle: product.shortDescription || (product.description ? product.description.replace(/<[^>]*>?/gm, '').substring(0, 150) + "..." : "Premium quality product."),
        image: product.images && product.images.length > 0 ? getImageUrl(product.images[0]) : "https://via.placeholder.com/1600x900?text=No+Image",
        cta: "View Details",
        ctaLink: `/product/${product._id}`,
        gradient: gradients[index % gradients.length],
        accent: "white",
        stats: [
            { icon: "fas fa-tag", text: "Best Price", sub: "Contact Us" },
            { icon: "fas fa-star", text: "Top Rated", sub: "Quality Assured" }
        ]
    }))];

    console.log('🎠 HeroSlider received featuredProducts:', featuredProducts.length);
    console.log('🎠 HeroSlider slides count:', slides.length);
    return (
        <div
            ref={containerRef}
            className="relative h-[550px] lg:h-[600px] w-full overflow-hidden group"
            onMouseMove={handleMouseMove}
        >
            <Swiper
                modules={[Autoplay, EffectFade, Navigation, Pagination]}
                effect="fade"
                speed={1000}
                autoplay={{
                    delay: 6000,
                    disableOnInteraction: false,
                }}
                navigation={{
                    nextEl: '.swiper-button-next',
                    prevEl: '.swiper-button-prev',
                }}
                pagination={{
                    clickable: true,
                    renderBullet: function (index, className) {
                        return '<span class="' + className + ' w-3 h-3 bg-white/50 hover:bg-white transition-all duration-300 transform hover:scale-125"></span>';
                    },
                }}
                loop={true}
                className="h-full w-full"
            >
                {slides.map((slide) => (
                    <SwiperSlide key={slide.id}>
                        <section className={`relative h-full w-full bg-gradient-to-br ${slide.gradient} text-white overflow-hidden`}>

                            {/* Motion Spotlight Effect */}
                            <div
                                className="absolute inset-0 pointer-events-none z-0"
                                style={{
                                    background: `radial-gradient(circle at ${mousePosition.x}% ${mousePosition.y}%, rgba(255, 255, 255, 0.15) 0%, transparent 40%), 
                                     radial-gradient(circle at ${100 - mousePosition.x}% ${100 - mousePosition.y}%, rgba(255, 255, 255, 0.05) 0%, transparent 40%)`
                                }}
                            />

                            {/* Parallax Background Image */}
                            <div className="absolute inset-0 opacity-40 mix-blend-overlay" style={{ overflow: 'hidden' }}>
                                <img
                                    src={slide.image}
                                    alt={slide.title}
                                    className="w-full h-full object-cover"
                                    style={{
                                        transform: `translate(${(mousePosition.x - 50) * 0.02}px, ${(mousePosition.y - 50) * 0.02}px) scale(1.05)`,
                                        transition: 'transform 0.1s ease-out',
                                        filter: 'brightness(0.8) contrast(1.2)'
                                    }}
                                />
                            </div>

                            {/* Pattern Overlay */}
                            <div className="absolute inset-0 opacity-20" style={{
                                backgroundImage: `repeating-linear-gradient(90deg, transparent, transparent 2px, rgba(255, 255, 255, 0.3) 2px, rgba(255, 255, 255, 0.3) 4px)`
                            }}></div>

                            {/* Floating Graphics (Shared) */}
                            <div className="absolute inset-0 overflow-hidden pointer-events-none">
                                <div className="absolute top-[20%] left-[10%] w-32 h-32 bg-white/10 rounded-full blur-3xl animate-pulse"></div>
                                <div className="absolute bottom-[20%] right-[10%] w-40 h-40 bg-blue-500/20 rounded-full blur-3xl animate-pulse delay-700"></div>
                            </div>

                            <div className="relative z-10 h-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center">
                                <div className="grid lg:grid-cols-5 gap-8 lg:gap-12 w-full items-center">
                                    {/* Text Content - 2 cols out of 5 = 40% */}
                                    <div className="lg:col-span-2" data-aos="fade-right">
                                        <div className="inline-block bg-white/20 backdrop-blur-sm px-4 py-2 rounded-full mb-6 border-l-4 border-db-red">
                                            <span className="text-sm font-semibold text-white flex items-center gap-2 uppercase tracking-wider">
                                                <i className="fas fa-star text-yellow-400"></i>
                                                {slide.type === 'main' ? 'Top Rated Automation' : 'Premium Quality'}
                                            </span>
                                        </div>
                                        <h1 className="text-3xl sm:text-5xl lg:text-6xl font-bold mb-6 leading-tight drop-shadow-lg"
                                            style={{
                                                transform: `translate(${(mousePosition.x - 50) * 0.01}px, ${(mousePosition.y - 50) * 0.01}px)`,
                                            }}
                                        >
                                            {slide.title}
                                        </h1>
                                        <p className="text-lg text-gray-200 mb-8 max-w-xl leading-relaxed font-light">
                                            {slide.subtitle}
                                        </p>

                                        {/* Stats/Features Grid */}
                                        <div className="flex gap-6 mb-8">
                                            {slide.stats.map((stat, idx) => (
                                                <div key={idx} className="flex items-center gap-3 bg-white/10 backdrop-blur-sm p-3 rounded-lg border border-white/10 hover:bg-white/20 transition-colors">
                                                    <div className={`w-10 h-10 rounded-full bg-white/20 flex items-center justify-center text-${slide.accent}`}>
                                                        <i className={stat.icon}></i>
                                                    </div>
                                                    <div>
                                                        <div className="font-bold text-sm">{stat.text}</div>
                                                        <div className="text-xs text-gray-300">{stat.sub}</div>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>

                                        <div className="flex flex-wrap gap-4">
                                            <Link
                                                to={slide.ctaLink}
                                                className="bg-db-red hover:bg-red-700 text-white px-8 py-4 rounded-lg font-bold text-lg transition-all duration-300 transform hover:scale-105 shadow-lg shadow-red-900/50 flex items-center gap-2"
                                            >
                                                {slide.cta} <i className="fas fa-arrow-right"></i>
                                            </Link>
                                        </div>
                                    </div>

                                    {/* Visuals (Right Side) - 3 cols out of 5 = 60% */}
                                    <div className="hidden lg:flex lg:col-span-3 relative h-full items-center justify-center" data-aos="fade-left">
                                        <div className="relative w-full">
                                            {slide.type === 'main' ? (
                                                /* 4-Section Grid for Main Slide */
                                                <div className="grid grid-cols-2 gap-4">
                                                    <Link to="/categories/barcode-printer" className="bg-white/10 backdrop-blur-md p-6 rounded-2xl border border-white/20 hover:bg-white/20 transition-all duration-300 transform hover:-translate-y-1 hover:shadow-2xl group/card text-center">
                                                        <i className="fas fa-print text-4xl text-white mb-3 group-hover/card:scale-110 transition-transform"></i>
                                                        <h3 className="font-bold text-lg mb-1">Printers</h3>
                                                        <p className="text-xs text-blue-200">Thermal & Barcode</p>
                                                    </Link>
                                                    <Link to="/categories/barcode-scanner" className="bg-white/10 backdrop-blur-md p-6 rounded-2xl border border-white/20 hover:bg-white/20 transition-all duration-300 transform hover:-translate-y-1 hover:shadow-2xl group/card text-center">
                                                        <i className="fas fa-barcode text-4xl text-white mb-3 group-hover/card:scale-110 transition-transform"></i>
                                                        <h3 className="font-bold text-lg mb-1">Scanners</h3>
                                                        <p className="text-xs text-blue-200">1D & 2D Capture</p>
                                                    </Link>
                                                    <Link to="/categories/label-printer" className="bg-white/10 backdrop-blur-md p-6 rounded-2xl border border-white/20 hover:bg-white/20 transition-all duration-300 transform hover:-translate-y-1 hover:shadow-2xl group/card text-center">
                                                        <i className="fas fa-tag text-4xl text-white mb-3 group-hover/card:scale-110 transition-transform"></i>
                                                        <h3 className="font-bold text-lg mb-1">Labels</h3>
                                                        <p className="text-xs text-blue-200">Premium Quality</p>
                                                    </Link>
                                                    <Link to="/categories/thermal-printer" className="bg-white/10 backdrop-blur-md p-6 rounded-2xl border border-white/20 hover:bg-white/20 transition-all duration-300 transform hover:-translate-y-1 hover:shadow-2xl group/card text-center">
                                                        <i className="fas fa-scroll text-4xl text-white mb-3 group-hover/card:scale-110 transition-transform"></i>
                                                        <h3 className="font-bold text-lg mb-1">Rolls</h3>
                                                        <p className="text-xs text-blue-200">All Sizes</p>
                                                    </Link>
                                                </div>
                                            ) : (
                                                /* Single Product Image for Other Slides (Cleaner Look) */
                                                <div className="relative z-10 p-6 w-full max-w-full overflow-hidden">
                                                    {/* Back Glow */}
                                                    <div className={`absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[120%] h-[120%] bg-${slide.accent === 'white' ? 'gray-500' : slide.accent}/20 blur-3xl rounded-full`}></div>

                                                    <div className="relative rounded-2xl overflow-hidden shadow-2xl border border-white/20 max-h-[400px]">
                                                        <img src={slide.image} alt={slide.title} className="w-full h-full max-h-[400px] object-contain hover:scale-105 transition-transform duration-700" />
                                                        <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-6">
                                                            <p className="text-white font-bold text-xl">{slide.type.charAt(0).toUpperCase() + slide.type.slice(1)} Series</p>
                                                            <p className="text-gray-300 text-sm">Professional Grade</p>
                                                        </div>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </section>
                    </SwiperSlide>
                ))}

                {/* Navigation Buttons */}
                <div className="swiper-button-prev !text-white !w-8 !h-8 !bg-white/10 !rounded-full !left-4 lg:!left-12 hover:!bg-db-red transition-all backdrop-blur-md hidden sm:flex justify-center items-center border border-white/20 group-hover:opacity-100 opacity-0" style={{ '--swiper-navigation-size': '14px' }}></div>
                <div className="swiper-button-next !text-white !w-8 !h-8 !bg-white/10 !rounded-full !right-4 lg:!right-12 hover:!bg-db-red transition-all backdrop-blur-md hidden sm:flex justify-center items-center border border-white/20 group-hover:opacity-100 opacity-0" style={{ '--swiper-navigation-size': '14px' }}></div>
            </Swiper>
        </div>
    );
};

export default HeroSlider;
